import { useMemo } from 'react';
import { CellType, CellValueDetailedType, DetailedCellError } from 'hyperformula';

import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import { CellData, CellFormulaInfo, HotComputedCellValue } from '../../../../common/table/TableTypes';
import type { MilanoteHotTableInstance } from '../tableHotInstanceTypes';

type MilanoteCellRendererFormulaInfoProps = {
    row: number;
    col: number;
    hotComputedCellValue: HotComputedCellValue;
    cellData?: CellData;
    hotTableInstance: MilanoteHotTableInstance;
};

const isDetailedCellError = (value: unknown): value is DetailedCellError =>
    typeof value === 'object' && value !== null && 'message' in value;

/**
 * Get formula metadata for a cell from HyperFormula.
 */
const useMilanoteCellRendererFormulaInfo = (
    props: MilanoteCellRendererFormulaInfoProps,
    deps?: ReadonlyArray<unknown>,
): CellFormulaInfo | undefined => {
    const { row, col, hotComputedCellValue, cellData = {}, hotTableInstance } = props;
    const { locale, elementId } = hotTableInstance.milanoteProps;

    return useMemo(
        () => {
            const sheet = hyperFormulaInstance.getSheetId(elementId);
            if (sheet === undefined) return undefined;

            const hfType = hyperFormulaInstance.getCellType({ sheet, row, col });
            const hfDetailedType = hyperFormulaInstance.getCellValueDetailedType({ sheet, row, col });
            const hfCellValue = hyperFormulaInstance.getCellValue({ sheet, row, col });

            const isFormula = hfType === CellType.FORMULA || hfType === CellType.ARRAYFORMULA;
            const isFormulaError =
                isFormula && hfDetailedType === CellValueDetailedType.ERROR && isDetailedCellError(hfCellValue);

            return { hfType, hfDetailedType, hfCellValue, isFormula, isFormulaError };
        },
        deps || [hotComputedCellValue, cellData, locale],
    );
};

export default useMilanoteCellRendererFormulaInfo;
